import { Component, DestroyRef, inject, signal, computed, AfterViewInit, effect, AfterRenderRef, afterNextRender, Injector, runInInjectionContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CacButtonComponent, CacFieldComponent, CacIconComponent, ItemRecord } from '@controladad/ng-base';
import { DashboardApiService } from '@core';
import { AdminDashboard, BestSellerProduct, TimeSeriesAmountPoint, TimeSeriesPoint, TopCustomer, TopSearchTerm } from '@models';
import { formControl, formGroup } from '@al00x/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as ApexChartsNS from 'apexcharts';

const ApexChartsCtor: any = (ApexChartsNS as any).default ?? (ApexChartsNS as any);

@Component({
  selector: 'lib-dashboard',
  standalone: true,
  imports: [CommonModule, CacIconComponent, CacButtonComponent, CacFieldComponent, MatProgressSpinnerModule],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements AfterViewInit {
  private readonly api = inject(DashboardApiService);
  private readonly destroyRef = inject(DestroyRef);

  isLoading = signal(false);
  data = signal<AdminDashboard | null>(null);
  private viewReady = signal(false);
  dateError = signal<string | null>(null);
  selectedRange = signal<number | null>(null);
  isThisYear = signal<boolean>(false);

  getVendorItems = this.api.getVendorItems();

  filters = formGroup({
    from: formControl<string | Date | null>(null),
    to: formControl<string | Date | null>(null),
    vendors: formControl<number[]>([]),
  });

  totalSalesAmount = computed(() => this.data()?.totalSalesAmount ?? 0);
  totalSalesCount = computed(() => this.data()?.totalSalesCount ?? 0);
  ordersCountByDate = computed<TimeSeriesPoint[]>(() => this.data()?.ordersCountByDate ?? []);
  ordersAmountByDate = computed<TimeSeriesAmountPoint[]>(() => this.data()?.ordersAmountByDate ?? []);
  topCustomers = computed<TopCustomer[]>(() => this.data()?.topCustomers ?? []);
  topSearchTerms = computed<TopSearchTerm[]>(() => this.data()?.topSearchTerms ?? []);
  bestSellers = computed<BestSellerProduct[]>(() => this.data()?.bestSellers ?? []);

  constructor() {
    // Re-render charts whenever data changes
    effect(() => {
      const ready = this.viewReady();
      const loaded = !this.isLoading();
      // read signal lengths to create dependency
      if (ready && loaded) this.renderCharts();
    });

    // Listen to form changes and auto-load data with debounce
    this.filters.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.updateSelectedRange();
      this.validateDatesAndLoad();
    });

    // Set initial range after setting up the listener
    this.setQuickRange(30);
  }

  validateDatesAndLoad() {
    const f = this.filters.getRawValue();

    // Clear previous error
    this.dateError.set(null);

    // Convert dates to comparable format
    let fromDate: Date | null = null;
    let toDate: Date | null = null;

    try {
      if (f.from) {
        if (f.from instanceof Date) {
          fromDate = f.from;
        } else if (typeof f.from === 'string') {
          const fromStr = f.from.trim();
          if (fromStr && /^\d{4}-\d{2}-\d{2}$/.test(fromStr)) {
            fromDate = new Date(fromStr + 'T00:00:00');
          }
        }
      }

      if (f.to) {
        if (f.to instanceof Date) {
          toDate = f.to;
        } else if (typeof f.to === 'string') {
          const toStr = f.to.trim();
          if (toStr && /^\d{4}-\d{2}-\d{2}$/.test(toStr)) {
            toDate = new Date(toStr + 'T23:59:59');
          }
        }
      }

      // Validate date range
      if (fromDate && toDate) {
        if (fromDate > toDate) {
          this.dateError.set('From date cannot be later than To date');
          return;
        }
      }

      // If validation passes, load data
      this.load();
    } catch (error) {
      console.error('[Dashboard] Date validation error:', error);
      this.dateError.set('Invalid date format');
    }
  }

  private load() {
    this.isLoading.set(true);
    const f = this.filters.getRawValue();
    console.log('[Dashboard] Form values:', f);

    // Validate and convert dates safely
    let from: string | undefined;
    let to: string | undefined;

    try {
      if (f.from) {
        let fromStr: string;

        // Handle different types that the date picker might return
        if (f.from instanceof Date) {
          fromStr = this.toDateInput(f.from);
        } else if (typeof f.from === 'string') {
          fromStr = f.from.trim();
        } else {
          console.error('[Dashboard] Unexpected from date type:', typeof f.from, f.from);
          this.isLoading.set(false);
          return;
        }

        if (fromStr && !/^\d{4}-\d{2}-\d{2}$/.test(fromStr)) {
          console.error('[Dashboard] Invalid from date format:', fromStr);
          this.isLoading.set(false);
          return;
        }

        if (fromStr) {
          const fromDate = new Date(fromStr + 'T00:00:00');
          if (isNaN(fromDate.getTime())) {
            console.error('[Dashboard] Invalid from date:', fromStr);
            this.isLoading.set(false);
            return;
          }
          from = fromDate.toISOString();
        }
      }

      if (f.to) {
        let toStr: string;

        // Handle different types that the date picker might return
        if (f.to instanceof Date) {
          toStr = this.toDateInput(f.to);
        } else if (typeof f.to === 'string') {
          toStr = f.to.trim();
        } else {
          console.error('[Dashboard] Unexpected to date type:', typeof f.to, f.to);
          this.isLoading.set(false);
          return;
        }

        if (toStr && !/^\d{4}-\d{2}-\d{2}$/.test(toStr)) {
          console.error('[Dashboard] Invalid to date format:', toStr);
          this.isLoading.set(false);
          return;
        }

        if (toStr) {
          const toDate = new Date(toStr + 'T23:59:59');
          if (isNaN(toDate.getTime())) {
            console.error('[Dashboard] Invalid to date:', toStr);
            this.isLoading.set(false);
            return;
          }
          to = toDate.toISOString();
        }
      }
    } catch (error) {
      console.error('[Dashboard] Date processing error:', error);
      this.isLoading.set(false);
      return;
    }

    this.api
      .getAdminDashboard(from, to, f.vendors ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.data.set(data);
          this.isLoading.set(false);
          // ensure charts render immediately after data arrives
          this.renderCharts();
        },
        error: () => {
          console.error('[Dashboard] Failed to load dashboard');
          this.isLoading.set(false);
        },
      });
  }
  private areaChart?: any;
  private barChart?: any;
  private donutChart?: any;
  private afterRef?: AfterRenderRef;
  private readonly injector = inject(Injector);

  ngAfterViewInit(): void {
    this.viewReady.set(true);
    this.renderCharts();
  }

  private renderCharts() {
    // Run after Angular paints using injection context (no setTimeout)
    this.afterRef?.destroy();
    runInInjectionContext(this.injector, () => {
      this.afterRef = afterNextRender(() => {
        try {
          const css = getComputedStyle(document.documentElement);
          const getVar = (name: string, fallback: string) => {
            const v = css.getPropertyValue(name).trim();
            return v.length ? v : fallback;
          };
          const primary = getVar('--primary', '#b91c1c');
          const accent = getVar('--accent', '#ef4444');
          const lightPrimary = `#${(() => { try { const c = primary.replace('#', ''); const r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16); const w = 0.6; const mix = (x: number) => Math.round(x + (255 - x) * w); return [mix(r), mix(g), mix(b)].map(v => v.toString(16).padStart(2, '0')).join(''); } catch { return 'e89a9a'; } })()}`;
          const gridColor = getVar('--outline', '#e5e7eb');
          const ordersCountEl = document.getElementById('ordersCountChart');
          const ordersAmountEl = document.getElementById('ordersAmountChart');
          const topTermsEl = document.getElementById('topSearchTermsChart');

          const countLen = this.ordersCountByDate().length;
          const amountLen = this.ordersAmountByDate().length;
          const termsLen = this.topSearchTerms().length;

          const buildXAxis = (el: HTMLElement | null, timestamps: number[]) => {
            if (!timestamps.length) return { type: 'datetime' } as any;
            const dayMs = 86400000;
            const minTs = Math.min(...timestamps);
            const maxTs = Math.max(...timestamps);
            const daysSpan = (maxTs - minTs) / dayMs;
            // Small ranges: lock ticks to whole days to prevent duplicate day labels
            if (daysSpan <= 14) {
              const min = Math.floor(minTs / dayMs) * dayMs; // start of day
              const max = Math.ceil((maxTs + 1) / dayMs) * dayMs; // end boundary
              const days = Math.max(1, Math.round((max - min) / dayMs));
              const dtf = new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short' });
              return {
                type: 'datetime',
                min,
                max,
                tickAmount: Math.min(12, days + 1),
                tickPlacement: 'between',
                labels: {
                  rotate: 0,
                  hideOverlappingLabels: true,
                  trim: true,
                  formatter: (val: string) => dtf.format(new Date(val)),
                },
              } as any;
            }
            // Larger ranges: adaptive monthly/yearly labeling
            const width = el?.clientWidth ?? 800;
            const tickAmount = Math.max(4, Math.min(12, Math.floor(width / 110)));
            const fmt: Intl.DateTimeFormatOptions = daysSpan > 365
              ? { month: 'short', year: '2-digit' }
              : { month: 'short', day: '2-digit' };
            const dtf = new Intl.DateTimeFormat(undefined, fmt);
            return {
              type: 'datetime',
              min: minTs,
              max: maxTs,
              tickAmount,
              labels: {
                rotate: 0,
                hideOverlappingLabels: true,
                trim: true,
                formatter: (val: string) => dtf.format(new Date(val)),
              },
            } as any;
          };

          if (ordersCountEl && countLen) {
            this.areaChart?.destroy();
            ordersCountEl.innerHTML = '';
            const countDates = this.ordersCountByDate().map(p => new Date(p.date).getTime());
            this.areaChart = new ApexChartsCtor(ordersCountEl, {
              chart: { type: 'bar', height: 260, toolbar: { show: false }, zoom: { enabled: false }, selection: { enabled: false } },
              dataLabels: { enabled: false },
              plotOptions: { bar: { columnWidth: '50%', borderRadius: 6 } },
              xaxis: buildXAxis(ordersCountEl, countDates),
              grid: { strokeDashArray: 4, borderColor: gridColor },
              colors: [lightPrimary],
              series: [
                { name: 'Orders', data: countDates.map((t, i) => ({ x: t, y: this.ordersCountByDate()[i].count })) }
              ],
              tooltip: { x: { format: 'dd MMM' } }
            });
            this.areaChart.render();
          }

          if (ordersAmountEl && amountLen) {
            this.barChart?.destroy();
            ordersAmountEl.innerHTML = '';
            const amountDates = this.ordersAmountByDate().map(p => new Date(p.date).getTime());
            this.barChart = new ApexChartsCtor(ordersAmountEl, {
              chart: { type: 'bar', height: 260, toolbar: { show: false }, zoom: { enabled: false }, selection: { enabled: false } },
              dataLabels: { enabled: false },
              plotOptions: { bar: { columnWidth: '50%', borderRadius: 6 } },
              xaxis: buildXAxis(ordersAmountEl, amountDates),
              grid: { strokeDashArray: 4, borderColor: gridColor },
              colors: [lightPrimary],
              series: [
                { name: 'Amount', data: amountDates.map((t, i) => ({ x: t, y: this.ordersAmountByDate()[i].amount })) }
              ],
              tooltip: { x: { format: 'dd MMM' } }
            });
            this.barChart.render();
          }

          if (topTermsEl && termsLen) {
            this.donutChart?.destroy();
            const labels = this.topSearchTerms().map(t => t.term);
            const series = this.topSearchTerms().map(t => t.count);
            topTermsEl.innerHTML = '';
            this.donutChart = new ApexChartsCtor(topTermsEl, {
              chart: { type: 'bar', height: 260, toolbar: { show: false }, zoom: { enabled: false }, selection: { enabled: false } },
              series: [{ name: 'Searches', data: series }],
              xaxis: { categories: labels },
              dataLabels: { enabled: false },
              grid: { strokeDashArray: 4, borderColor: gridColor },
              colors: [lightPrimary],
              plotOptions: { bar: { borderRadius: 6, columnWidth: '50%' } },
            });
            this.donutChart.render();
          }
        } catch (err) {
          console.error('[Dashboard] Chart render error', err);
        }
      });
    });
  }

  // UI helpers
  setQuickRange(days: number) {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (days - 1));
    this.filters.patchValue({
      from: this.toDateInput(from),
      to: this.toDateInput(to)
    }, { emitEvent: false }); // Don't emit event to avoid debounce delay

    // Set the selected range
    this.selectedRange.set(days);
    this.isThisYear.set(false);

    // Load immediately for quick range buttons
    this.validateDatesAndLoad();
  }

  setThisYearRange() {
    const to = new Date();
    const from = new Date(to.getFullYear(), 0, 1); // January 1st of current year
    this.filters.patchValue({
      from: this.toDateInput(from),
      to: this.toDateInput(to)
    }, { emitEvent: false }); // Don't emit event to avoid debounce delay

    // Set the this year flag
    this.selectedRange.set(null);
    this.isThisYear.set(true);

    // Load immediately for quick range buttons
    this.validateDatesAndLoad();
  }

  private updateSelectedRange() {
    const f = this.filters.getRawValue();

    if (!f.from || !f.to) {
      this.selectedRange.set(null);
      this.isThisYear.set(false);
      return;
    }

    try {
      // Convert form values to date strings
      let fromStr: string;
      let toStr: string;

      if (f.from instanceof Date) {
        fromStr = this.toDateInput(f.from);
      } else if (typeof f.from === 'string') {
        fromStr = f.from.trim();
      } else {
        this.selectedRange.set(null);
        this.isThisYear.set(false);
        return;
      }

      if (f.to instanceof Date) {
        toStr = this.toDateInput(f.to);
      } else if (typeof f.to === 'string') {
        toStr = f.to.trim();
      } else {
        this.selectedRange.set(null);
        this.isThisYear.set(false);
        return;
      }

      // Check if current range matches any of the quick ranges
      const today = new Date();
      const todayStr = this.toDateInput(today);
      const thisYearStart = this.toDateInput(new Date(today.getFullYear(), 0, 1));

      // Check if it's "This Year" range
      if (fromStr === thisYearStart && toStr === todayStr) {
        this.selectedRange.set(null);
        this.isThisYear.set(true);
        return;
      }

      // Check if 'to' date is today for other quick ranges
      if (toStr === todayStr) {
        // Calculate days difference
        const fromDate = new Date(fromStr);
        const toDate = new Date(toStr);
        const diffTime = toDate.getTime() - fromDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates

        // Check if it matches any of our quick ranges
        if (diffDays === 7 || diffDays === 30 || diffDays === 90) {
          this.selectedRange.set(diffDays);
          this.isThisYear.set(false);
          return;
        }
      }

      // If no match found, clear selection
      this.selectedRange.set(null);
      this.isThisYear.set(false);
    } catch (error) {
      console.error('[Dashboard] Error updating selected range:', error);
      this.selectedRange.set(null);
      this.isThisYear.set(false);
    }
  }

  isRangeSelected(days: number): boolean {
    return this.selectedRange() === days;
  }

  isThisYearSelected(): boolean {
    return this.isThisYear();
  }

  private toDateInput(d: Date): string {
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    this.afterRef?.destroy();
    this.areaChart?.destroy?.();
    this.barChart?.destroy?.();
    this.donutChart?.destroy?.();
  }
}


